import * as React from "react";
import { PDFDownloadLink } from '@react-pdf/renderer'
import { useAuth } from "../../../App";
import { EmergencyTreatmentApi } from "../../../api/EmergencyTreatmentApi";
import { useParams, useHistory } from "react-router";
import { IEmergencyTreatmentReport, ISaveEmergencyTreatmentReportModel, IEmergencyTreatmentsModel } from "../../../models/IEmergencyTreatment";
import { CustomButton, ButtonType, SpinnerWrapper, AlertMessage, AlertType, Form, CustomSubmitButton, CustomSubmitButtonType } from "soprasteria-ui-library";
import { setMessage } from "../../../helpers/MessageHelper";
import { EmergencyTreatmentFormInputs } from "./EmergencyTreatmentFormInputs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { IPatient } from "../../../models/IPatient";
import { EmergencyTreatmentPDF } from "../../PDFSheets/EmergencyTreatmentPDF/EmergencyTreatmentPDF";
import { isInRole, UserContext } from "../../../contexts/UserContext";
import { ALLMÄNTANDVÅRDBAS } from "../../../utils/RoleConstants";
import { formatPersonalSecurityNumberAndOrganisationNumber } from "../../../helpers/PersonalSecurityNumberAndOrganisationNumberHelper";
import { AutoSave } from '../../AutoSave/AutoSave';

interface IEmergencyTreatmentReportView {
    patient: IPatient;
}

export const EmergencyTreatmentReport: React.FunctionComponent<IEmergencyTreatmentReportView> = ({ patient }) => {
    const auth = useAuth();
    const api = new EmergencyTreatmentApi(auth);
    const history = useHistory();
    const { id } = useParams<{id: string}>();
    const abortController = new AbortController();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [isApiCallPending, setIsApiCallPending] = React.useState<boolean>(false);
    const [emergencyTreatmentReport, setEmergencyTreatmentReport] = React.useState<IEmergencyTreatmentReport>();
    const [isEmergencyTreatmentReportRegister, setIsEmergencyTreatmentReportRegister] = React.useState<boolean>(false);
    const [isEmergencyTreatmentReportSaving, setIsEmergencyTreatmentReportSaving] = React.useState<boolean>(false);
    const [reportedTreatments, setReportedTreatments] = React.useState<IEmergencyTreatmentsModel>();
    const userInfo = React.useContext(UserContext);
    const hasRoleAllmantandvard = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const saveButtonId = "save-emergency-treatment-button";

    React.useEffect(() => {
        if (id) {
            getEmergencyTreatmentReport(id, false);
        }

        return () => {
            abortController.abort();
        }
    }, [])

    const getEmergencyTreatmentReport = (id: string, refresh: boolean) => {
        setIsApiCallPending(true);
        api.getEmergencyTreatmentReport(id, refresh, (data: IEmergencyTreatmentReport) => {
            setEmergencyTreatmentReport(data);
        }, (message) => {
            setErrorMessage(message);
        }, () => {
            setIsApiCallPending(false);
        });
    }

    const saveEmergencyTreatmentReport = (data: ISaveEmergencyTreatmentReportModel) => {
        setIsEmergencyTreatmentReportSaving(true);
        api.saveEmergencyTreatmentReport(data, () => {
            const message = setMessage("Akuträkningen har sparats", successMessage ?? "");
            setSuccessMessage(message);
            getEmergencyTreatmentReport(data.id.toString(), true);
        }, (error) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        }, () => {
            setIsEmergencyTreatmentReportSaving(false);
        });
    }

    const registerEmergencyTreatmentReport = (data: ISaveEmergencyTreatmentReportModel) => {
        setIsEmergencyTreatmentReportRegister(true);
        api.registerEmergencyTreatmentReport(data, () => {
            const message = setMessage("Akuträkningen har registrerats", successMessage ?? "");
            setSuccessMessage(message);
            getEmergencyTreatmentReport(data.id.toString(), true);
        }, (error) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        }, () => {
            setIsEmergencyTreatmentReportRegister(false);
        });
    }

    const updateReportedTreatments = (treatments: IEmergencyTreatmentsModel) => {
        setReportedTreatments(treatments);
    }

    return (
        <SpinnerWrapper isLoading={isApiCallPending}>
            <Form>
                <section className="card">
                    <div className="card-header">
                        <div className="row">
                            <div className="col">
                                <h2>Akuträkning {emergencyTreatmentReport ? `- ${emergencyTreatmentReport.treatmentNumber}` : ""}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                        <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                        <EmergencyTreatmentFormInputs emergencyTreatmentReport={emergencyTreatmentReport} patient={patient} reportedTreatments={reportedTreatments} updateReportedTreatments={updateReportedTreatments} />
                    </div>
                    <div className="card-footer">
                        <div className="row">
                            <div className="col-6 text-left">
                                <CustomButton buttonType={ButtonType.general} onClick={() => history.goBack()} buttonText="Gå tillbaka" />
                            </div>
                            <div className="col-6 text-right">
                                {
                                    React.useMemo(() =>
                                        hasRoleAllmantandvard && emergencyTreatmentReport ?
                                            <PDFDownloadLink
                                                document={
                                                    <EmergencyTreatmentPDF
                                                        emergencyTreatmentReport={emergencyTreatmentReport}
                                                        patientSocialSecurityNumber={formatPersonalSecurityNumberAndOrganisationNumber(patient.personalSecurityNumber)}
                                                        patientName={patient.name}
                                                        reportedTreatments={reportedTreatments}
                                                    />}
                                                fileName="Akuträkning.pdf"
                                            >
                                                {({ blob, url, loading, error }) => loading ? "" : <CustomButton buttonType={ButtonType.pdf} buttonText="Exportera till PDF" />}
                                            </PDFDownloadLink>
                                            : null
                                        , [hasRoleAllmantandvard, emergencyTreatmentReport, reportedTreatments])
                                }
                                {
                                    !emergencyTreatmentReport?.isRegistered && emergencyTreatmentReport?.isEditable ?
                                        <>
                                            <AutoSave saveButtonId={saveButtonId} />
                                            <CustomSubmitButton isLoading={isEmergencyTreatmentReportSaving} buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={saveEmergencyTreatmentReport} id={saveButtonId}><FontAwesomeIcon icon={faSave} /> Spara</CustomSubmitButton>
                                            <CustomSubmitButton isLoading={isEmergencyTreatmentReportRegister} onButtonSubmit={registerEmergencyTreatmentReport}>Påbörja vårdrapportering</CustomSubmitButton>
                                        </>
                                        : null
                                }
                            </div>
                        </div>
                    </div>
                </section>
            </Form>
        </SpinnerWrapper>
    );
}